import { makeStyles } from '@material-ui/core/styles';
import { EntityInfoCard } from '@backstage/plugin-catalog-react';
import {
  LikeDislikeButtons,
  StarredRatingButtons,
} from '@backstage-community/plugin-entity-feedback';

/*
 * "Rate this entity", in the portal's card grammar.
 *
 * The plugin renders the thumbs in a core-components `InfoCard` — a MUI Paper —
 * while every other card in the info column (About, Group profile, Ownership) is
 * a BUI `Card`. On a Repository page the two sit stacked, so the difference in
 * header rule, radius and padding reads as a broken card rather than a variant.
 * Swapping the shell for `EntityInfoCard` is what unifies them; the prompt and
 * the button geometry are the rest of the pass.
 *
 * The buttons themselves stay the plugin's — they own the rating API call and
 * the follow-up dialog — and are re-skinned in place from bare 48px MUI icon
 * buttons to the portal's 30px square control.
 */
const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexFlow: 'column nowrap',
    gap: 12,
  },
  prompt: {
    fontSize: 12.5,
    lineHeight: 1.5,
    color: 'var(--bui-fg-secondary)',
  },
  buttons: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,

    '& [class*="MuiIconButton-root"]': {
      width: 30,
      height: 30,
      padding: 0,
      borderRadius: 8,
      color: 'var(--bui-fg-secondary)',
      boxShadow: 'var(--hone-portal-elevation-raised)',
    },
    '& [class*="MuiIconButton-root"]:hover': {
      background: 'var(--hone-portal-fill-inset)',
      color: 'var(--bui-fg-primary)',
    },
    '& [class*="MuiIconButton-root"] [class*="MuiSvgIcon-root"]': {
      fontSize: 15,
    },
  },
});

/** Mirrors the config schema of `entity-card:entity-feedback/ratings-buttons`. */
export type EntityRatingsCardProps = {
  title: string;
  variant: 'like-dislike' | 'starred';
  requestResponse?: boolean;
  dialogTitle?: string;
  dialogResponses?: Array<{ id: string; label: string }>;
};

export function EntityRatingsCard(props: EntityRatingsCardProps) {
  const { title, variant, requestResponse, dialogTitle, dialogResponses } =
    props;
  const classes = useStyles();
  const Buttons =
    variant === 'starred' ? StarredRatingButtons : LikeDislikeButtons;

  return (
    <EntityInfoCard title={title}>
      <div className={classes.root}>
        <span className={classes.prompt}>
          Ratings tell the owning team which entities people rely on.
        </span>
        <div className={classes.buttons}>
          <Buttons
            requestResponse={requestResponse}
            feedbackDialogTitle={dialogTitle}
            feedbackDialogResponses={dialogResponses}
          />
        </div>
      </div>
    </EntityInfoCard>
  );
}
